// src/screens/QC/QcRelease.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectUser } from "../../features/userSlice";

// Reuse styles you've been using on mobile (container/buttons/etc.)
import styles from "../UnloadVehicle/UnloadingEntry.module.css";
import FloatingActions from "../../components/FloatingActions";

const fmtDateTime = (iso) => (iso ? dayjs(iso).format("DD-MMM-YYYY HH:mm") : "");
const fmtNum = (n) => (n === null || n === undefined ? "" : Number(n).toFixed(2));

// Minimal toast/alert helper (swap with PopupContext later if needed)
const notify = ({ variant = "info", title = "", message = "", autocloseMs }) => {
  if (variant === "error") window.alert(`${title ? title + ": " : ""}${message || "Something went wrong"}`);
  else if (title || message) console.log(`[${variant}] ${title} ${message}`);
};

const QcRelease = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser); // { emp_id, token, ... }

  const token = user?.token || "";
  const empId = user?.emp_id;
  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [entity, setEntity] = useState(""); // optional filter (kept for parity)
  const [q, setQ] = useState("");           // future search (server not using now)
  const [selected, setSelected] = useState(new Set());

  // For simple mobile paging
  const pageSize = 10;
  const [visibleCount, setVisibleCount] = useState(pageSize);

  const fetchData = async () => {
    if (!token || !empId) return;
    setLoading(true);
    try {
      const payload = { emp_id: empId };
      const { data } = await axios.post(`/api/qc/getQcPending`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setRows(data?.data || []);
      setVisibleCount(pageSize);
    } catch (err) {
      console.error(err);
      notify({ variant: "error", title: "Error", message: "Failed to load QC pending" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filteredRows = useMemo(() => {
    // Keep hook ready for when you enable search/filter server-side.
    let list = rows;
    if (entity) list = list.filter((r) => String(r.entity_code || "").includes(entity));
    if (q?.trim()) {
      const qq = q.trim().toLowerCase();
      list = list.filter(
        (r) =>
          String(r.docnumber || "").toLowerCase().includes(qq) ||
          String(r.P_truck || "").toLowerCase().includes(qq) ||
          String(r.P_PARTY_NAME || "").toLowerCase().includes(qq) ||
          String(r.p_item_name || "").toLowerCase().includes(qq)
      );
    }
    return list;
  }, [rows, entity, q]);

  const visibleRows = filteredRows.slice(0, visibleCount);
  const hasMore = visibleCount < filteredRows.length;

  const keyOf = (r) => `${r.entity_code}::${r.docnumber}`;

  const toggleSelect = (k) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };

  const releaseOne = async (row) => {
    try {
      const payload = { entity_code: row.entity_code, docnumber: row.docnumber, released_by: empId };
      const { data } = await axios.post(`/api/qc/releaseQcRequest`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (data?.ok) {
        notify({ variant: "success", title: "Released", message: `QC released: ${row.docnumber}`, autocloseMs: 1200 });
        await fetchData();
      } else {
        notify({ variant: "error", title: "Failed", message: data?.message || "Release failed" });
      }
    } catch (err) {
      console.error(err);
      notify({ variant: "error", title: "Error", message: "Release API failed" });
    }
  };

  const releaseSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Confirm release for ${selected.size} selected document(s)?`)) return;

    let success = 0;
    for (const k of selected) {
      const [entity_code, docnumber] = k.split("::");
      try {
        await axios.post(
          `/api/qc/releaseRequest`,
          { entity_code, docnumber, released_by: empId },
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        success++;
      } catch (e) {
        console.error("Release failed for", docnumber, e);
      }
    }
    setSelected(new Set());
    notify({ variant: "success", title: "Bulk Release", message: `Released ${success}/${selected.size} document(s).`, autocloseMs: 1200 });
    fetchData();
  };

  const onSearch = async (e) => {
    e.preventDefault();
    await fetchData(); // server currently ignores q/entity; kept for future
  };

  // 🔒 Login Guard UI
  if (!token) {
    return (
      <div className={styles.approveContainer}>
        <p>You must be logged in to manage assignments.</p>
        <button className={styles.cancelBtn} onClick={() => navigate("/")}>
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className={styles.approveContainer} style={{ paddingBottom: 80 }}>
      <div ref={topRef} />
      <div className={styles.screenTitle}>QC Release</div>

      {/* Filters (mobile-friendly single row wrap) */}
      <form onSubmit={onSearch} className={styles.filters} style={{ gap: 8 }}>
        <input
          className={styles.select}
          placeholder="Entity (optional)"
          value={entity}
          onChange={(e) => setEntity(e.target.value.toUpperCase())}
          style={{ height: 36, fontSize: 14 }}
        />
        <input
          className={styles.select}
          placeholder="Search Doc/Truck/Party/Item"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ height: 36, fontSize: 14 }}
        />

        <div style={{ display: "flex", gap: 8 }}>
          <button className={styles.cancelBtn} type="submit" style={{ flex: 1, backgroundColor: "#0d6efd" }}>
            {loading ? "Loading..." : "Search"}
          </button>
          <button
            type="button"
            className={styles.cancelBtn}
            style={{ flex: 1, backgroundColor: "#6c757d" }}
            onClick={fetchData}
          >
            Refresh
          </button>
        </div>

        <button
          type="button"
          className={styles.cancelBtn}
          style={{
            backgroundColor: selected.size ? "#198754" : "#9fbfac",
            opacity: selected.size ? 1 : 0.7,
          }}
          disabled={selected.size === 0}
          onClick={releaseSelected}
        >
          Release Selected ({selected.size})
        </button>

        <div style={{ fontSize: 12, color: "#444", textAlign: "right" }}>
          Total: <b>{filteredRows.length}</b>
        </div>
      </form>

      {/* Cards list (mobile) */}
      <div style={{ display: "grid", gap: 10 }}>
        {visibleRows.map((r) => {
          const k = keyOf(r);
          const checked = selected.has(k);
          return (
            <div
              key={k}
              style={{
                background: "#fff",
                borderRadius: 10,
                padding: 12,
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                display: "grid",
                gap: 6,
              }}
            >
              {/* Header row: Doc + checkbox */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  {r.docnumber} <span style={{ color: "#777", fontWeight: 400 }}>({r.entity_code})</span>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleSelect(k)}
                    style={{ width: 16, height: 16 }}
                  />
                  Select
                </label>
              </div>

              {/* Key facts row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 13 }}>
                <div><b>Truck:</b> {r.P_truck || "-"}</div>
                <div><b>Party:</b> {r.P_PARTY_NAME || "-"}</div>
                <div><b>Item:</b> {r.p_item_name || "-"}</div>
                <div><b>Challan Wt:</b> {fmtNum(r.P_CHALLAN_WT)}</div>
                <div><b>Load Pt:</b> {r.P_LOAD_POINT || "-"}</div>
                <div><b>Location:</b> {r.location_name || r.location_code || "-"}</div>
              </div>

              {/* Meta row */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 12, color: "#555" }}>
                <div><b>Req By:</b> {r.qc_requested_by || "-"}</div>
                <div><b>Req On:</b> {fmtDateTime(r.qc_request_time)}</div>
              </div>

              {/* Action row */}
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <button
                  className={styles.cancelBtn}
                  style={{ flex: 1, backgroundColor: "#198754" }}
                  onClick={() => releaseOne(r)}
                >
                  Release
                </button>
              </div>
            </div>
          );
        })}

        {visibleRows.length === 0 && !loading && (
          <div style={{ textAlign: "center", color: "#666", fontStyle: "italic", marginTop: 10 }}>
            No pending records
          </div>
        )}
      </div>

      {/* Load more for mobile */}
      {hasMore && (
        <div style={{ marginTop: 12 }}>
          <button
            className={styles.cancelBtn}
            onClick={() => setVisibleCount((c) => c + pageSize)}
            style={{ backgroundColor: "#0d6efd" }}
          >
            Load More
          </button>
        </div>
      )}

      <FloatingActions topRef={topRef} bottomRef={bottomRef} />
      <div ref={bottomRef} />
    </div>
  );
};

export default QcRelease;
